import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import '../styles/Chatbot.css';

const Chatbot = () => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    // Persist session_id in sessionStorage so refreshes don't break the thread
    const stored = sessionStorage.getItem('ubie_session_id');
    if (stored) return stored;
    const newId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    sessionStorage.setItem('ubie_session_id', newId);
    return newId;
  });
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I\'m Ubie, the UnsupervisedBias AI assistant. I can help you explore the site, learn about our services, or answer questions. What can I help you with?'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastIntent, setLastIntent] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '40px'; // Reset to min height
      const newHeight = Math.min(textarea.scrollHeight, 120); // Max 120px
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: 'Hi! I\'m Ubie, the UnsupervisedBias AI assistant. I can help you explore the site, learn about our services, or answer questions. What can I help you with?'
      }
    ]);
    setLastIntent(null);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Send message to backend
      const API_URL = '/api';
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/core/chatbot/message/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_history: messages.slice(-5),
          session_id: sessionId,
          page_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from chatbot');
      }

      const data = await response.json();

      // Add assistant response to chat
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.message 
      }]);

      // Keep session_id in sync (backend echoes it back)
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem('ubie_session_id', data.session_id);
      }

      // Store intent for action buttons
      if (data.intent) {
        setLastIntent(data.intent);
      }

    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting right now. Please try again or reach out to support@unsupervisedbias.com'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = (action) => {
    if (action.startsWith('mailto:')) {
      window.location.href = action;
    } else if (action.startsWith('http')) {
      window.open(action, '_blank');
    } else {
      // Internal route
      navigate(action);
      setIsOpen(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const renderActionButtons = () => {
    if (!lastIntent || !lastIntent.action) return null;

    const buttonConfig = {
      account_signup: {
        text: '✨ Create Account',
        className: 'action-btn signup-btn'
      },
      book_call: {
        text: '📅 Book Consultation',
        className: 'action-btn booking-btn'
      },
      support: {
        text: '✉️ Email Support',
        className: 'action-btn support-btn'
      }
    };

    const config = buttonConfig[lastIntent.type];
    if (!config) return null;

    return (
      <div className="action-buttons">
        <p className="action-prompt">{lastIntent.message}</p>
        <button
          className={config.className}
          onClick={() => handleAction(lastIntent.action)}
        >
          {config.text}
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Chat Bubble */}
      <div
        className={`chat-bubble ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-content">
              <h3>Ubie</h3>
              <span className="chat-status">{isAuthenticated ? 'Online' : 'Members only'}</span>
            </div>
            <div className="chat-header-actions">
              {isAuthenticated && (
                <button
                  onClick={clearChat}
                  className="chat-action-btn"
                  title="Clear chat"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              )}
              <button
                className="chat-action-btn"
                onClick={() => setIsOpen(false)}
                title="Close chat"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Auth gate for guests */}
          {!isAuthenticated ? (
            <div className="chat-auth-gate">
              <div className="chat-auth-gate-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <p className="chat-auth-gate-title">Create an account to chat with Ubie</p>
              <p className="chat-auth-gate-sub">Ubie is available to registered members.</p>
              <button
                className="chat-auth-btn primary"
                onClick={() => { navigate('/register'); setIsOpen(false); }}
              >
                Create Account
              </button>
              <button
                className="chat-auth-btn secondary"
                onClick={() => { navigate('/signin'); setIsOpen(false); }}
              >
                Sign In
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="chat-messages">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`message ${msg.role}`}
                  >
                    <div className="message-content">
                      {msg.role === 'assistant'
                        ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                        : msg.content}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="message assistant">
                    <div className="message-content typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Action Buttons */}
              {renderActionButtons()}

              {/* Input */}
              <form className="chat-input-form" onSubmit={handleSendMessage}>
                <textarea
                  ref={textareaRef}
                  className="chat-input"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  rows={1}
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={isLoading || !inputMessage.trim()}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
